import React from 'react';
import { RefreshCw, LogOut, Loader2 } from 'lucide-react';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import AppSidebar, { SCREENS } from '@/components/app/AppSidebar.jsx';
import DateRangePicker, { presetRange } from '@/components/app/DateRangePicker.jsx';
import ThemeToggle from '@/components/app/ThemeToggle.jsx';
import { MockBadge } from '@/components/app/kit.jsx';
import Login from '@/components/app/Login.jsx';
import { OpsProvider, useOps } from '@/hooks/useOps.jsx';
import { isMock } from '@/lib/ops-data.js';
import { currentProfile, signOutOps as signOut } from '@/lib/medplum.js';

import Overview from '@/screens/Overview.jsx';
import ByProvider from '@/screens/ByProvider.jsx';
import Attribution from '@/screens/Attribution.jsx';
import Pricing from '@/screens/Pricing.jsx';
import ManualCosts from '@/screens/ManualCosts.jsx';
import AiQuality from '@/screens/AiQuality.jsx';
import HarnessOps from '@/screens/HarnessOps.jsx';
import Economics from '@/screens/Economics.jsx';

const SCREEN_COMPONENTS = {
  overview: Overview,
  provider: ByProvider,
  attribution: Attribution,
  pricing: Pricing,
  manual: ManualCosts,
  quality: AiQuality,
  harness: HarnessOps,
  economics: Economics,
};

function HeaderRefresh() {
  const { reload, loading } = useOps();
  return (
    <Button variant="outline" size="sm" onClick={reload} disabled={loading}>
      <RefreshCw className={`size-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
    </Button>
  );
}

function Console({ onSignOut }) {
  const mock = isMock();
  const [screen, setScreen] = React.useState('overview');
  const [range, setRange] = React.useState(() => presetRange('30d'));
  const Active = SCREEN_COMPONENTS[screen];
  const title = (SCREENS.find((s) => s.key === screen) || {}).label || '';

  return (
    <OpsProvider range={range}>
      <SidebarProvider>
        <AppSidebar active={screen} onNavigate={setScreen} />
        <SidebarInset className="min-w-0">
          <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b bg-background/95 px-4 backdrop-blur">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-1 h-5" />
            <div className="text-[13.5px] font-semibold text-foreground">{title}</div>
            {mock && <MockBadge />}
            <div className="ml-auto flex items-center gap-2">
              <DateRangePicker range={range} onChange={setRange} />
              <HeaderRefresh />
              <ThemeToggle />
              <Button variant="ghost" size="sm" onClick={onSignOut} title="Sign out">
                <LogOut className="size-4" /> <span className="hidden sm:inline">Sign out</span>
              </Button>
            </div>
          </header>
          <main className="min-w-0 flex-1 overflow-x-hidden">
            <Active range={range} setScreen={setScreen} />
          </main>
        </SidebarInset>
      </SidebarProvider>
    </OpsProvider>
  );
}

export default function App() {
  // Mock mode skips login entirely (contract-friendly demo). Otherwise resolve the
  // stored Medplum session on boot.
  const [auth, setAuth] = React.useState(isMock() ? 'in' : 'checking');

  React.useEffect(() => {
    if (isMock()) return;
    let alive = true;
    currentProfile().then((p) => { if (alive) setAuth(p ? 'in' : 'out'); }).catch(() => { if (alive) setAuth('out'); });
    return () => { alive = false; };
  }, []);

  const onSignOut = async () => {
    try { await signOut(); } catch (_) { /* local clear is authoritative */ }
    setAuth('out');
  };

  if (auth === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 size-5 animate-spin" /> Restoring session…
      </div>
    );
  }
  if (auth === 'out') return <Login onSignedIn={() => setAuth('in')} />;
  return <Console onSignOut={onSignOut} />;
}
